import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Logo } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult({ cancelled = false }) {
  const [status, setStatus] = useState("pending");
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let stop = false;
    let tries = 0;
    const poll = async () => {
      while (!stop && tries < 15) {
        try {
          const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setStatus("paid");
            return;
          }
          if (data.payment_status === "failed" || data.payment_status === "expired") {
            setStatus("failed");
            return;
          }
        } catch {
          setStatus("error");
          return;
        }
        tries += 1;
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!stop) setStatus((s) => (s === "paid" ? s : "timeout"));
    };
    poll();
    return () => {
      stop = true;
    };
  }, [cancelled, sessionId]);

  const body = cancelled
    ? {
        icon: <XCircle className="h-16 w-16 text-amber-300" strokeWidth={1.5} />,
        title: "Payment cancelled",
        text: "No charge was made. Your booking request is still with us — reply to any GameLens email or book again to pick a new date.",
      }
    : status === "paid"
    ? {
        icon: <CheckCircle2 className="h-16 w-16 text-emerald-400" strokeWidth={1.5} />,
        title: "Payment received",
        text: "Thank you! Your payment went through and your booking is locked in. A record is with the GameLens team.",
      }
    : status === "failed"
    ? {
        icon: <XCircle className="h-16 w-16 text-red-300" strokeWidth={1.5} />,
        title: "Payment didn't go through",
        text: "The payment failed or expired. Reply to your GameLens email and we'll send a fresh payment link.",
      }
    : status === "error"
    ? {
        icon: <XCircle className="h-16 w-16 text-red-300" strokeWidth={1.5} />,
        title: "Couldn't check payment",
        text: "We couldn't verify the payment status right now. If your card was charged, don't worry — we'll confirm by email.",
      }
    : {
        icon: <Loader2 className="h-16 w-16 animate-spin text-accentlight" strokeWidth={1.5} />,
        title: "Processing payment…",
        text: "Confirming with the card network. This usually takes a few seconds.",
      };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-6 font-body text-white" data-testid="payment-result-page">
      <div className="w-full max-w-sm rounded-2xl border border-white/5 bg-surface p-10 text-center">
        <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-surface-hover text-accentlight">
          <Logo className="h-9 w-9" />
        </span>
        <div className="mb-6 flex justify-center">{body.icon}</div>
        <h1 className="font-display text-3xl tracking-wider">{body.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-white/60">{body.text}</p>
        <a
          href="/"
          className="mt-8 inline-block rounded-full border border-accentlight/40 px-6 py-3 text-xs font-bold uppercase tracking-wider text-accentlight transition-colors hover:bg-accentlight/10"
        >
          Back to site
        </a>
      </div>
    </div>
  );
}
